/**
 * PATH: packages/email-hub/src/feature/components/email-preview-pane/PreviewHeader.tsx
 * FILE_ID: EMAILHUB-EMAILPREVIEWPANE-HEADER-001
 * PURPOSE: Render preview chrome and responsive action controls.
 */
import { useState } from 'react'
import { Maximize2, MoreHorizontal, Search, X } from 'lucide-react'
import { useSortedByActionUsage } from '@quotech/ui'
import { cn } from '../../../_internal/lib/utils'
import { PingTeammateButton } from '../PingTeammateButton'
import { MOBILE_PINNED_ACTION_IDS } from './usePreviewToolbarActions'
import type { PreviewHeaderProps, PreviewToolbarAction } from './types'

interface ActionButtonProps {
  action: PreviewToolbarAction
  trackPreviewAction: (actionId: string) => void
}

function ActionButton({ action, trackPreviewAction }: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={() => action.onClick(() => trackPreviewAction(action.id))}
      disabled={action.disabled}
      className={cn(
        'p-1.5 rounded-md hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50',
        action.destructive && 'text-destructive',
      )}
      title={action.title}
      aria-label={action.title}
      data-testid={action.testId}
    >
      {action.icon}
    </button>
  )
}

export function PreviewHeader({
  email,
  onClose,
  showCloseAction,
  onFullView,
  onRefetch,
  pingOpen,
  setPingOpen,
  previewActions: previewToolbarActions,
  find,
}: PreviewHeaderProps) {
  const [moreOpen, setMoreOpen] = useState(false)
  const { sorted: sortedPreviewActions, onActionUsed: trackPreviewAction } =
    useSortedByActionUsage(
      'quotech.email.preview-toolbar',
      previewToolbarActions,
      (action) => action.id,
    )
  const pinnedPreviewActions = previewToolbarActions.filter((action) =>
    MOBILE_PINNED_ACTION_IDS.includes(action.id),
  )
  const overflowPreviewActions = previewToolbarActions.filter(
    (action) => !MOBILE_PINNED_ACTION_IDS.includes(action.id),
  )

  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/20">
      <div className="flex items-center gap-2 min-w-0">
        {showCloseAction && (
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-accent"
            title="Close preview"
            aria-label="Close preview"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
        <h2
          className="text-sm font-semibold text-foreground truncate"
          title={email.subject || '(no subject)'}
        >
          {email.subject || '(no subject)'}
        </h2>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <PingTeammateButton
          emailId={email.id}
          onPinged={() => onRefetch?.()}
          open={pingOpen}
          onOpenChange={setPingOpen}
        />
        <div className="hidden md:flex items-center gap-1">
          {sortedPreviewActions.map((action) => (
            <ActionButton
              key={action.id}
              action={action}
              trackPreviewAction={trackPreviewAction}
            />
          ))}
        </div>
        <div className="flex md:hidden items-center gap-1">
          {pinnedPreviewActions.map((action) => (
            <ActionButton
              key={action.id}
              action={action}
              trackPreviewAction={trackPreviewAction}
            />
          ))}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMoreOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              aria-label="More actions"
              className="p-1.5 rounded-md hover:bg-accent"
              title="More actions"
              data-testid="email-preview-more"
            >
              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
            </button>
            {moreOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setMoreOpen(false)}
                  aria-hidden
                />
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-1 z-50 min-w-[176px] rounded-md border border-border bg-popover text-popover-foreground shadow-lg py-1"
                >
                  {overflowPreviewActions.map((action) => (
                    <button
                      key={action.id}
                      role="menuitem"
                      type="button"
                      disabled={action.disabled}
                      onClick={() => {
                        action.onClick(() => trackPreviewAction(action.id))
                        setMoreOpen(false)
                      }}
                      className={cn(
                        'flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50',
                        action.destructive && 'text-destructive',
                      )}
                      title={action.title}
                      data-testid={action.testId}
                    >
                      <span className="shrink-0">{action.icon}</span>
                      <span className="truncate">{action.title}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="w-px h-4 bg-border mx-1" />
        <button
          type="button"
          onClick={() => find.open()}
          className="p-1.5 rounded-md hover:bg-accent"
          title="Find in thread (⌘F / Ctrl+F)"
          aria-label="Find in thread"
          data-testid="preview-find-button"
        >
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
        <button
          type="button"
          onClick={() => {
            trackPreviewAction('full_view')
            onFullView()
          }}
          className="p-1.5 rounded-md hover:bg-accent"
          title="Open full view"
          aria-label="Open full view"
        >
          <Maximize2 className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>
    </div>
  )
}
